// Summary: Frontend KDS API client for queue retrieval, line actions, and print preview endpoints.
// Details: docs/function-library/web/services.md#services-kds
import { authenticatedInstance } from '../index/index.service';
import type { KdsPrintLine, OrderPrintLogItemView } from 'shared-models/browser';

export type KdsBootstrapView = {
  tenantId: string;
  status: 'ready';
  message: string;
};

export type KitchenStatusValue = 'COOKING' | 'READY' | 'DONE' | 'HIDE';

export type KdsSelectionView = {
  id: number;
  itemName: string;
  groupName: string;
  quantity: number;
  groupType: 'variant' | 'combo';
  itemPk?: number | null;
  kds?: boolean | null;
  parentItemPk?: number | null;
  kitchenStatus: KitchenStatusValue | null;
  wasPulledBack: boolean;
  wasReprinted: boolean;
  createdAt: string;
  updatedAt: string;
};

export type KdsOrderlineView = {
  id: number;
  tenantId: string;
  orderId: number;
  productId: number;
  productName: string;
  categoryId: number | null;
  categoryName: string | null;
  unitPrice: number;
  quantity: number;
  status: string;
  kitchenStatus: KitchenStatusValue | null;
  wasPulledBack: boolean;
  wasReprinted: boolean;
  note?: string | null;
  selectionLabels?: string[];
  selections?: KdsSelectionView[];
  createdAt: string;
  updatedAt: string;
};

export type KdsOrderView = {
  id: number;
  tenantId: string;
  sessionId: number;
  sessionName: string;
  tableId: number | null;
  tableNumber: string | null;
  areaId: number | null;
  areaName: string | null;
  orderType: 'dine_in' | 'take_away';
  orderCode: string;
  status: string;
  isVip: boolean;
  dinersCount: number;
  createdAt: string;
  updatedAt: string;
  orderlines: KdsOrderlineView[];
  waiterName?: string | null;
};

export type KdsOrderPrintPreviewPayload = {
  orderId: number;
  orderNumber: string;
  tableNumber: string;
  dinersCount: number;
  printerName: string;
  printTime: number;
  orderlines: KdsPrintLine[];
  waiterName: string | null;
  tableNumberAtBottom: boolean;
};

export type KdsOrderPrintPreviewResponse = {
  previewId: string;
  expiresAt: number;
  payload: KdsOrderPrintPreviewPayload;
};

type ErrorWithResponseMessage = {
  response?: {
    data?: {
      message?: unknown;
    };
  };
};

// Summary: Normalizes Axios-like and generic thrown errors into a single
function extractApiErrorMessage(error: unknown, fallback: string): string {
  const message = (error as ErrorWithResponseMessage | null | undefined)?.response?.data?.message;
  if (typeof message === 'string' && message.trim().length > 0) {
    return message;
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return fallback;
}

class KdsService {
  // Summary: Fetches tenant-scoped KDS bootstrap payload from backend scaffold endpoint. This service is the dedicated frontend entry point for future KDS data loading and keeps KDS networking isolated from PosShellPage services.
  async getTenantBootstrap(tenantId: string, employeeSession: string): Promise<KdsBootstrapView> {
    try {
      const response = await authenticatedInstance.get<KdsBootstrapView>(`/kds/tenant/${encodeURIComponent(tenantId)}/bootstrap`, {
        headers: { 'x-employee-session': employeeSession },
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Failed loading KDS bootstrap'));
    }
  }

  // Summary: Loads all open orders with their sent orderlines (kitchen_status included) for the active tenant session. Called once on KDS page mount for initial hydration; subsequent changes arrive via SSE deltas (Rule 23). Returns the full KdsOrderView list so the KDS screen can render order cards immediately without further fetching.
  async listOrders(tenantId: string, employeeSession?: string): Promise<KdsOrderView[]> {
    try {
      // No employee session = JWT-only viewer; authenticatedInstance's JWT authorizes the read.
      const response = await authenticatedInstance.get<KdsOrderView[]>(`/kds/tenant/${encodeURIComponent(tenantId)}/orders`, {
        headers: employeeSession ? { 'x-employee-session': employeeSession } : undefined,
      });
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Failed loading KDS orders'));
    }
  }

  // Summary: Sends a single batch kitchen_status PATCH for multiple orderlines in
  async batchUpdateKitchenStatus(
    tenantId: string,
    orderlineIds: number[],
    status: KitchenStatusValue | null,
    employeeSession?: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch(
        `/kds/tenant/${encodeURIComponent(tenantId)}/orderlines/batch-status`,
        { orderlineIds, status },
        { headers: employeeSession ? { 'x-employee-session': employeeSession } : undefined },
      );
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Failed updating kitchen status in batch'));
    }
  }

  // Summary: Batch kitchen_status PATCH for combo selections (independent ready/done per
  async batchUpdateSelectionKitchenStatus(
    tenantId: string,
    selectionIds: number[],
    status: KitchenStatusValue | null,
    employeeSession?: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.patch(
        `/kds/tenant/${encodeURIComponent(tenantId)}/selections/batch-status`,
        { selectionIds, status },
        { headers: employeeSession ? { 'x-employee-session': employeeSession } : undefined },
      );
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Failed updating selection kitchen status'));
    }
  }

  // Summary: Queues a KDS order print job to the shared print_jobs pipeline
  async createOrderPrintPreview(
    tenantId: string,
    orderId: number,
    printerId: number,
    employeeSession?: string,
    orderlineIds?: number[],
  ): Promise<KdsOrderPrintPreviewResponse> {
    try {
      const response = await authenticatedInstance.post<KdsOrderPrintPreviewResponse>(
        `/kds/tenant/${encodeURIComponent(tenantId)}/order/${orderId}/print-preview`,
        orderlineIds && orderlineIds.length > 0 ? { printerId, orderlineIds } : { printerId },
        { headers: employeeSession ? { 'x-employee-session': employeeSession } : undefined },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Failed creating print preview'));
    }
  }

  async cancelOrderPrintPreview(
    tenantId: string,
    orderId: number,
    previewId: string,
    employeeSession?: string,
  ): Promise<void> {
    try {
      await authenticatedInstance.post<void>(
        `/kds/tenant/${encodeURIComponent(tenantId)}/order/${orderId}/print-preview/cancel`,
        { previewId },
        { headers: employeeSession ? { 'x-employee-session': employeeSession } : undefined },
      );
    } catch {
    }
  }

  async queueOrderPrint(
    tenantId: string,
    orderId: number,
    printerId: number,
    employeeSession: string | undefined,
    previewId?: string,
    orderlineIds?: number[],
    actionGroupId?: string,
    logPrinterName?: string,
    logItems?: OrderPrintLogItemView[],
    printableImageDataUrl?: string,
    kind?: 'green_fire' | 'reprint',
    actingEmployeeId?: number | null,
  ): Promise<void> {
    try {
      const body: {
        printerId: number;
        previewId?: string;
        orderlineIds?: number[];
        actionGroupId?: string;
        logPrinterName?: string;
        logItems?: OrderPrintLogItemView[];
        printableImageDataUrl?: string;
        kind?: 'green_fire' | 'reprint';
      } = { printerId };
      if (previewId) body.previewId = previewId;
      if (orderlineIds && orderlineIds.length > 0) body.orderlineIds = orderlineIds;
      if (actionGroupId) body.actionGroupId = actionGroupId;
      if (logPrinterName) body.logPrinterName = logPrinterName;
      if (logItems && logItems.length > 0) body.logItems = logItems;
      if (printableImageDataUrl) body.printableImageDataUrl = printableImageDataUrl;
      if (kind) body.kind = kind;

      await authenticatedInstance.post<void>(
        `/kds/tenant/${encodeURIComponent(tenantId)}/order/${orderId}/print`,
        body,
        {
          headers: employeeSession
            ? {
              'x-employee-session': employeeSession,
              ...(actingEmployeeId != null ? { 'x-acting-employee-id': String(actingEmployeeId) } : {}),
            }
            : undefined,
        },
      );
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Failed queueing print job'));
    }
  }

  async claimGreenFireRescue(
    tenantId: string,
    rescueId: string,
    employeeSession?: string,
  ): Promise<{ claimed: boolean }> {
    try {
      const response = await authenticatedInstance.post<{ claimed: boolean }>(
        `/kds/tenant/${encodeURIComponent(tenantId)}/green-fire/rescue/${encodeURIComponent(rescueId)}/claim`,
        undefined,
        { headers: employeeSession ? { 'x-employee-session': employeeSession } : undefined },
      );
      return response.data;
    } catch (error: unknown) {
      throw new Error(extractApiErrorMessage(error, 'Failed claiming green fire rescue'));
    }
  }

}

export const kdsService = new KdsService();
